import type { ReactNode } from 'react';
import { createSelector, type OutputSelector } from 'reselect';
// eslint-disable-next-line lodash/import-scope
import type { Dictionary } from 'lodash';
import { isSystemFieldType } from '@jira/polaris__domain-field/src/isSystemFieldType.tsx';
import type { FieldType } from '@jira/polaris__domain-field/src/FieldType.types.tsx';
import type {
	Field,
	FieldConfiguration,
	FieldKey,
	FieldMap,
} from '@jira/polaris__domain-field/src/FieldKey.types.tsx';
import type { PolarisPlay } from '@jira/polaris__domain-field/src/PolarisPlayContribution.types.tsx';
import { iconForPolarisFieldType } from '@jira/polaris__component-icons/src/iconForPolarisFieldType.tsx';
import { parseAdfString } from '@jira/polaris__lib-adf-utils/src/parseAdfString.tsx';
import type { Ari } from '@jira/platform__utils-ari/src/index.tsx';
import type { IconProps } from '@atlaskit/icon/types';
import type { State } from './FieldsUpdate.types.tsx';
import { getAllFieldArray, getAllFields, getFields } from './getFieldArray.tsx';

export const createGetFieldByKey = (
	fieldKey?: FieldKey,
): OutputSelector<State, Field | undefined, (res: Dictionary<Field>) => Field | undefined> =>
	createSelector(getFields, (fields) => (fieldKey ? fields[fieldKey] : undefined));

export const createGetAllFieldByKey = (
	fieldKey?: FieldKey,
): OutputSelector<State, Field | undefined, (res: FieldMap) => Field | undefined> =>
	createSelector(getAllFields, (fields) => (fieldKey ? fields[fieldKey] : undefined));

export const createGetFieldType = (
	fieldKey?: FieldKey,
): OutputSelector<
	State,
	FieldType | undefined,
	(res: Dictionary<Field>) => FieldType | undefined
> =>
	createSelector(getFields, (fields) =>
		fieldKey !== undefined ? fields[fieldKey]?.type : undefined,
	);

export const createGetFieldLabel = (
	fieldKey?: FieldKey,
): OutputSelector<State, string | undefined, (res: Dictionary<Field>) => string | undefined> =>
	createSelector(getFields, (fields) =>
		fieldKey !== undefined ? fields[fieldKey]?.label : undefined,
	);

export const createGetIsSystemField = (
	fieldKey?: FieldKey,
): OutputSelector<State, boolean, (res: Dictionary<Field>) => boolean> =>
	createSelector(getFields, (fields) => {
		if (fieldKey === undefined) {
			return false;
		}

		const field = fields[fieldKey];
		// field can be undefined because when we delete a field via sidenav it gets optimistically removed from the store and is not present in response from getFields
		return field ? isSystemFieldType(field.type) : false;
	});

export const createGetFieldPlay = (
	fieldKey?: FieldKey,
): OutputSelector<
	State,
	PolarisPlay | undefined,
	(res: Dictionary<Field>) => PolarisPlay | undefined
> =>
	createSelector(getFields, (fields) =>
		fieldKey !== undefined ? fields[fieldKey]?.play : undefined,
	);

export const createGetEditable = (
	fieldKey?: FieldKey,
): OutputSelector<State, boolean, (res: Dictionary<Field>) => boolean> =>
	createSelector(getFields, (fields) =>
		fieldKey !== undefined ? fields[fieldKey]?.editable : false,
	);

export const createGetFieldOfType = (
	ofType: FieldType,
): OutputSelector<State, Field | undefined, (res: Field[]) => Field | undefined> =>
	createSelector(getAllFieldArray, (fields) => fields.find((field) => field.type === ofType));

export const createGetIsDeletable = (
	fieldKey?: FieldKey,
): OutputSelector<State, boolean, (res: Dictionary<Field>) => boolean> =>
	createSelector(getFields, (fields) => {
		if (fieldKey === undefined || fields[fieldKey] === undefined) {
			return false;
		}
		return !isSystemFieldType(fields[fieldKey].type);
	});

export const createGetFieldDescription = (
	fieldKey: FieldKey,
): OutputSelector<
	State,
	{
		value: string;
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		adf: any;
		fixed: boolean;
	},
	(res: Dictionary<Field>) => {
		value: string;
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		adf: any;
		fixed: boolean;
	}
> =>
	createSelector(getFields, (fields) => {
		const fixedDescriptionFields = [
			'summary',
			'assignee',
			'created',
			'creator',
			'key',
			'reporter',
			'status',
			'updated',
			'labels',
		];

		const value = fields[fieldKey]?.description ?? '';

		return {
			value,
			adf: parseAdfString(value),
			fixed: fixedDescriptionFields.includes(fieldKey),
		};
	});

export const createGetFieldEmoji = (
	fieldKey: FieldKey,
): OutputSelector<State, string | undefined, (res: Dictionary<Field>) => string | undefined> =>
	createSelector(getFields, (fields) => fields[fieldKey]?.emoji);

export const createGetFieldByPlayId = (
	playId: Ari,
): OutputSelector<State, Field | undefined, (res: Dictionary<Field>) => Field | undefined> =>
	createSelector(getFields, (fields) =>
		Object.keys(fields)
			.map((key) => fields[key])
			.find(({ play }) => play?.id === playId),
	);

export const createGetFieldKeysOfType = (
	ofType: FieldType,
): OutputSelector<State, string[], (res: Field[]) => string[]> =>
	createSelector(getAllFieldArray, (fields) =>
		fields.filter(({ type }) => ofType === type).map(({ key }) => key),
	);

export const createGetFieldConfiguration = (
	fieldKey: FieldKey,
): OutputSelector<
	State,
	FieldConfiguration | undefined,
	(res: Field | undefined) => FieldConfiguration | undefined
> => {
	const getFieldByKey = createGetFieldByKey(fieldKey);

	return createSelector(getFieldByKey, (field) => field?.configuration);
};

export const createGetFieldHidden = (
	fieldKey: FieldKey,
): OutputSelector<State, boolean, (res: FieldConfiguration | undefined) => boolean> => {
	const getFieldConfiguration = createGetFieldConfiguration(fieldKey);

	return createSelector(getFieldConfiguration, (configuration) => configuration?.hidden ?? false);
};

export const createGetFieldRestricted = (
	fieldKey: FieldKey,
): OutputSelector<State, boolean, (res: Field | undefined) => boolean> => {
	const getFieldByKey = createGetFieldByKey(fieldKey);

	return createSelector(getFieldByKey, (field) => !!field?.hasRestrictedContext);
};

export const createGetFieldOptionWeightTypeIsSet = (
	fieldKey: FieldKey,
): OutputSelector<State, boolean, (res: FieldConfiguration | undefined) => boolean> => {
	const getFieldConfiguration = createGetFieldConfiguration(fieldKey);

	return createSelector(
		getFieldConfiguration,
		(configuration) => configuration?.optionWeightType !== undefined,
	);
};

export const getFieldTypeIcon = (
	fieldKey: FieldKey,
	iconProps?: IconProps,
): OutputSelector<
	State,
	ReactNode,
	(res1: FieldType | undefined, res2: FieldConfiguration | undefined) => ReactNode
> => {
	const getFieldType = createGetFieldType(fieldKey);
	const getFieldConfiguration = createGetFieldConfiguration(fieldKey);

	return createSelector(getFieldType, getFieldConfiguration, (fieldType, configuration) =>
		iconForPolarisFieldType(fieldType, iconProps, configuration),
	);
};
